import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * CandyButton — pill, 2px border, hard shadow, bouncy press.
 * variant: "candy" (purple) | "paper" (white) | "pink" | "yellow" | "mint" | "ink" (dark)
 * size: "sm" | "md" | "lg"
 * withArrow: renders a circular arrow icon inside a chip on the right
 */
const ICON_SIZES = { sm: 14, md: 16, lg: 18 };

export const CandyButton = ({
  as,
  to,
  href,
  children,
  onClick,
  type = "button",
  variant = "candy",
  size = "md",
  block = false,
  disabled = false,
  withArrow = false,
  icon: Icon,
  iconPosition = "left",
  iconClassName = "",
  className = "",
  "data-testid": testId,
  ...rest
}) => {
  const sizes = {
    sm: "min-h-[40px] text-[13px] px-4 py-2 gap-2",
    md: "min-h-[48px] text-sm px-5 py-3 gap-2.5",
    lg: "min-h-[56px] text-base px-6 py-3.5 gap-3",
  };
  const variants = {
    candy: "bg-candy text-white border-ink is-candy",
    paper: "bg-white text-ink border-ink",
    pink: "bg-pink text-ink border-ink is-pink",
    yellow: "bg-yellow text-ink border-ink",
    mint: "bg-mint text-ink border-ink",
    ink: "bg-ink text-white border-ink",
  };
  const arrowChip = {
    candy: "bg-white text-ink",
    paper: "bg-ink text-white",
    pink: "bg-white text-ink",
    yellow: "bg-ink text-white",
    mint: "bg-white text-ink",
    ink: "bg-candy text-white",
  };
  const base = `candy-press inline-flex items-center justify-center font-display font-bold tracking-tight border-2 rounded-full shadow-sticker select-none whitespace-nowrap ${
    disabled ? "opacity-50 cursor-not-allowed pointer-events-none" : "cursor-pointer"
  } ${block ? "w-full" : ""} ${sizes[size]} ${variants[variant]} ${className}`;

  const iconNode = Icon ? (
    <Icon size={ICON_SIZES[size]} strokeWidth={2.5} className={`shrink-0 ${iconClassName}`} />
  ) : null;

  const content = (
    <>
      {iconNode && iconPosition === "left" ? iconNode : null}
      {children != null ? <span>{children}</span> : null}
      {iconNode && iconPosition === "right" ? iconNode : null}
      {withArrow ? (
        <span
          className={`ml-1 inline-flex h-7 w-7 items-center justify-center rounded-full border-2 border-ink ${arrowChip[variant]} icon-wiggle`}
        >
          <ArrowRight size={14} strokeWidth={3} />
        </span>
      ) : null}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={base} data-testid={testId} {...rest}>
        {content}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={base} data-testid={testId} {...rest}>
        {content}
      </a>
    );
  }
  const Tag = as || "button";
  return (
    <Tag
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={base}
      data-testid={testId}
      {...rest}
    >
      {content}
    </Tag>
  );
};

export default CandyButton;
